import { Alert, Button, Group, Loader, NumberInput, Paper, Stack, Switch, Text, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActivitySettings, useDesktopSettings, useSaveActivitySettings, useSaveDesktopSettings } from '@/api/activity';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';

/** Admin-Einstellungen der Standorterfassung mit Datenschutz-Hinweisen (ACT-01/04). */
export function ActivitySettings() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data } = useActivitySettings(ctx.workspaceId);
  const save = useSaveActivitySettings(ctx.workspaceId);
  const onError = useApiError();
  const [enabled, setEnabled] = useState(false);
  const [interval, setInterval] = useState<number | string>(300);
  const [retention, setRetention] = useState<number | string>(90);
  useEffect(() => {
    if (data) {
      setEnabled(data.gpsEnabled);
      setInterval(data.intervalSeconds);
      setRetention(data.retentionDays);
    }
  }, [data]);
  const gpsInPlan = ctx.hasFeature('GPS');
  if (!data) return <Loader size="sm" />;

  return (
    <Stack maw={720}>
      <Paper withBorder p="md">
        <Title order={5} mb="xs">
          {t('activity.settingsTitle')}
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          {t('activity.settingsHint')}
        </Text>
        {!gpsInPlan && (
          <Alert color="gray" mb="md">
            {t('settings.notInPlan')}
          </Alert>
        )}
        <Stack>
          <Switch
            label={t('activity.gpsEnabled')}
            description={t('activity.gpsEnabledHint')}
            checked={enabled}
            disabled={!gpsInPlan}
            onChange={(e) => setEnabled(e.currentTarget.checked)}
          />
          <Group grow>
            <NumberInput label={t('activity.interval')} suffix=" s" min={30} max={3600} value={interval} onChange={setInterval} />
            <NumberInput label={t('activity.retention')} suffix={` ${t('activity.days')}`} min={1} max={3650} value={retention} onChange={setRetention} />
          </Group>
          {data?.gpsEnabled && !enabled && (
            <Alert color="red" variant="light">
              {t('activity.disableWarning')}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { gpsEnabled: enabled, intervalSeconds: Number(interval) || 300, retentionDays: Number(retention) || 90 },
                  { onError },
                )
              }
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Paper>
      <DesktopSettingsPanel />
    </Stack>
  );
}

/** Screenshots (ACT-02) und App-/URL-Erfassung (ACT-03) des Desktop-Clients. */
function DesktopSettingsPanel() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const inPlan = ctx.hasFeature('SCREENSHOTS');
  const { data } = useDesktopSettings(ctx.workspaceId);
  const save = useSaveDesktopSettings(ctx.workspaceId);
  const onError = useApiError();
  const [form, setForm] = useState({ screenshotsEnabled: false, blur: false, retentionDays: 30 as number | string, appTrackingEnabled: false });
  useEffect(() => {
    if (data) setForm({ screenshotsEnabled: data.screenshotsEnabled, blur: data.blur, retentionDays: data.retentionDays, appTrackingEnabled: data.appTrackingEnabled });
  }, [data]);
  if (!data) return null;
  const switchingOff = (data.screenshotsEnabled && !form.screenshotsEnabled) || (data.appTrackingEnabled && !form.appTrackingEnabled);
  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        {t('activity.desktopTitle')}
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        {t('activity.desktopHint')}
      </Text>
      {!inPlan && (
        <Alert color="gray" mb="md">
          {t('settings.notInPlan')}
        </Alert>
      )}
      <Stack>
        <Switch label={t('activity.screenshotsEnabled')} checked={form.screenshotsEnabled} disabled={!inPlan} onChange={(e) => setForm({ ...form, screenshotsEnabled: e.currentTarget.checked })} />
        <Switch label={t('activity.blur')} description={t('activity.blurHint')} checked={form.blur} disabled={!inPlan || !form.screenshotsEnabled} onChange={(e) => setForm({ ...form, blur: e.currentTarget.checked })} />
        <Switch label={t('activity.appTrackingEnabled')} description={t('activity.appTrackingHint')} checked={form.appTrackingEnabled} disabled={!inPlan} onChange={(e) => setForm({ ...form, appTrackingEnabled: e.currentTarget.checked })} />
        <NumberInput label={t('activity.desktopRetention')} suffix={` ${t('activity.days')}`} min={1} max={365} value={form.retentionDays} onChange={(v) => setForm({ ...form, retentionDays: v })} w={240} />
        {switchingOff && (
          <Alert color="red" variant="light">
            {t('activity.desktopDisableWarning')}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            loading={save.isPending}
            onClick={() => save.mutate({ ...form, retentionDays: Number(form.retentionDays) || 30 }, { onError })}
          >
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
